import { Trans } from '@lingui/react/macro';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DAY_COLUMNS } from '@/domain/templates/day-of-week';
import { formatHoursDisplay } from '@/domain/templates/preview-utils';
import { getShiftColour, SHIFT_PILL_CLASSES } from '@/domain/templates/shift-colours';
import { cn } from '@/lib/utils';
import type { ShiftSet } from '@/types/template';

import { ShiftTooltipContent } from './shift-tooltip-content';

/**
 * Collapsible preview row — header strip + 7-day pill grid. Shared
 * between the Shift Type and Staff Group preview tabs so both surfaces
 * render with the same layout, count + hours badges, and collapse
 * behaviour. The caller owns the collapsed state (URL/local) and the
 * 7-bucket pill matrix.
 *
 * The header carries an optional coloured swatch (used by the Shift
 * Type tab where every row has an intrinsic colour); Staff Group rows
 * omit it because pills within a group can be of multiple types.
 */
export function CollapsiblePreviewSection({
  name,
  swatchColor,
  perDay,
  totalShifts,
  totalHours,
  collapsed,
  onToggle,
  rowTestId,
  cellTestIdPrefix,
}: CollapsiblePreviewSectionProps) {
  return (
    <div data-testid={rowTestId}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onClick={onToggle}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onToggle()}
        className="bg-muted/30 hover:bg-muted/50 flex cursor-pointer items-center gap-3 p-3 transition-colors"
      >
        {collapsed ? (
          <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
        ) : (
          <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
        )}
        {swatchColor && <div className={cn('h-4 w-4 shrink-0 rounded-full', swatchColor)} />}
        <span className="text-[length:var(--body-font-size)] leading-[var(--body-line-height)] font-semibold">
          {name}
        </span>
        <Badge variant="secondary" className="text-sm">
          {totalShifts === 1 ? <Trans>1 shift</Trans> : <Trans>{totalShifts} shifts</Trans>}
        </Badge>
        <Badge variant="outline" className="ml-auto text-sm">
          <Trans>{formatHoursDisplay(totalHours)} hours total</Trans>
        </Badge>
      </div>

      {!collapsed && (
        <div className="grid grid-cols-7 divide-x">
          {DAY_COLUMNS.map((column, index) => {
            const cellShifts = perDay[index];
            return (
              <div
                key={column.key}
                data-testid={`${cellTestIdPrefix}-${column.key}`}
                className="flex min-h-14 flex-col justify-center gap-1 p-2"
              >
                {cellShifts.map((shift, i) => (
                  <ShiftPill key={`${shift.id}-${i}`} shift={shift} />
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * A single coloured pill showing the shift's start–end time. Hovering
 * reveals a tooltip with the full staffing details (method, groups,
 * tags, assigned employee) via {@link ShiftTooltipContent}. The pill's
 * colour is derived per-shift so the same shift renders the same
 * colour wherever it appears (Shift Type tab, Staff Group tab,
 * preview modal).
 */
export function ShiftPill({ shift }: ShiftPillProps) {
  const colour = SHIFT_PILL_CLASSES[getShiftColour(resolveShiftColorKey(shift))];
  const assignedEmployee =
    shift.staffing.method === 'specific' ? shift.staffing.employee.name : undefined;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'w-full cursor-default truncate rounded-md px-2 py-1 text-center text-sm font-semibold',
            'transition-all hover:scale-105 hover:shadow-lg',
            colour.solid,
          )}
        >
          {shift.startTime}-{shift.endTime}
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-[280px] rounded-lg bg-gray-800 p-4 text-white shadow-xl"
      >
        <ShiftTooltipContent shift={shift} assignedEmployee={assignedEmployee} />
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Canonical colour-seed for a shift. Used by both preview tabs so the
 * same shift always hashes to the same swatch. Prefer the shift-type
 * display name; fall back to the user-given shift name; last resort is
 * the shiftType id (the UUID).
 */
export function resolveShiftColorKey(shift: ShiftSet): string {
  return shift.shiftType.name || shift.name || shift.shiftType.id;
}

interface CollapsiblePreviewSectionProps {
  /** Display name shown in the header. */
  name: string;
  /**
   * Tailwind background-color class for the leading swatch dot.
   * Omit when the section doesn't have an intrinsic colour (e.g.
   * staff-group sections whose pills are coloured per-shift).
   */
  swatchColor?: string;
  /** 7 buckets of shifts indexed 0 = Mon … 6 = Sun. */
  perDay: ReadonlyArray<ShiftSet[]>;
  /** Sum of staff-instances across the week. */
  totalShifts: number;
  /** Sum of (duration × staff-instances) across the week, in hours. */
  totalHours: number;
  collapsed: boolean;
  onToggle: () => void;
  /** `data-testid` for the wrapper row. */
  rowTestId: string;
  /** Prefix for each day-cell `data-testid` — cells append `-${columnKey}`. */
  cellTestIdPrefix: string;
}

interface ShiftPillProps {
  shift: ShiftSet;
}
